import { useState, useEffect, useRef, memo } from "react";
import { useContext, useMemo } from "react";
import LevelTransmitterIndicator from "../components/LevelTransmitterIndicator";
import Transmitter from "../components/Transmitter";
import ControlValveControlPanel from "../components/ControlValveControlPanel";
import Valve from "../components/Valve";
import "./FurnacePreHeat.css";
import { WellContext } from "../context/WellContext";
import { AlarmContext } from "../context/AlarmContext";
import { PumpContext } from "../context/PumpContext";
import { ProcessContext } from "../context/ProcessContext";
import Pump from "../components/Pump";
import PumpFaceplate from "../components/PumpFaceplate";
import MDSFaceplate from "../components/MDSFaceplate";
import { PlantStore } from "../engine/PlantStore";
import { usePlantStore } from "../engine/usePlantStore";
import { trackPageVisit } from "../competency/PageTracker";

import TransmitterFaceplate from "../components/TransmitterFaceplate";
import FurnacePerformance from "./FurnacePerformance";






function ValveOutput({
    tag,
    value,
    x,
    y,
    r = 0,
    description = "Control Valve Opening"
}) {

    const [hover, setHover] = useState(false);
    const [showFaceplate, setShowFaceplate] = useState(false);

    return (
        <>
            {/* Hover */}
            {hover && (
                <div
                    style={{
                        position: "absolute",
                        left: x + 30,
                        top: y - 5,
                        background: "rgba(0,80,180,.85)",
                        color: "#fff",
                        padding: "5px 8px",
                        borderRadius: "4px",
                        border: "1px solid #4fc3ff",
                        fontSize: "11px",
                        zIndex: 9999,
                        pointerEvents: "none",
                        whiteSpace: "nowrap"
                    }}
                >
                    <strong>{tag}</strong><br />
                    {description}
                </div>
            )}

            {/* Original Valve Output */}
            <div
                onMouseEnter={() => setHover(true)}
                onMouseLeave={() => setHover(false)}
                onClick={() => setShowFaceplate(true)}
                style={{
                    position: "absolute",
                    left: x,
                    top: y,

                    color: "lime",
                    background: "black",

                    fontSize: "10px",
                    padding: "1px 4px",

                    borderRadius: "4px",
                    border: "1px solid #555",

                    transform: `rotate(${r}deg)`,
                    transformOrigin: "center",

                    cursor: "pointer",

                    fontWeight: "bold",

                    minWidth: "22px",
                    textAlign: "center",

                    transition: "0.2s",
                    boxShadow: hover
                        ? "0 0 8px rgba(0,255,255,.7)"
                        : "none"
                }}
            >
                {Number(value).toFixed(1)} %
            </div>

            {showFaceplate && (
                <TransmitterFaceplate
                    tag={tag}
                    value={Number(value)}
                    unit="%"
                    min={0}
                    max={100}
                    HH={100}
                    H={90}
                    L={10}
                    LL={0}
                    description={description}
                    x={x + 35}
                    y={y + 20}
                    onClose={() => setShowFaceplate(false)}
                />
            )}
        </>
    );
}




function FurnacePreHeat() {
  // ✅ Use ref to track render count
  const renderCount = useRef(0);
  renderCount.current += 1;
  console.log(`FurnacePreHeat Render: ${renderCount.current}`);

  // ✅ Track page visit only once
  useEffect(() => {
    trackPageVisit("FurnacePreHeat");
  }, []);

  const { toggleValve, valves } = useContext(WellContext);
  const [hoveredValve, setHoveredValve] = useState(null);
  const {processData,  setProcessData} = useContext(ProcessContext);
   const [showPerformance,setShowPerformance] = useState(false);


  const DEFAULT_VALVES = {
    FCV_20011: { mode: "Manual", opening: 0, sp: "", pv: "" },
  };

  const [valveData, setValveData] = useState(() => {
    const saved = localStorage.getItem("FurnacePreHeat_valveData");
    if (!saved) return DEFAULT_VALVES;
    try {
      const parsed = JSON.parse(saved);
      return {
        FCV_20011: { ...DEFAULT_VALVES.FCV_20011, ...parsed.FCV_20011 },
      };
    } catch {
      return DEFAULT_VALVES;
    }
  });

  /* ✅ Persist */
  useEffect(() => {
    localStorage.setItem("FurnacePreHeat_valveData", JSON.stringify(valveData));
  }, [valveData]);

  const updateValveData = (tag, field, value) => {
    setValveData(prev => ({
      ...prev,
      [tag]: {
        ...(prev[tag] || DEFAULT_VALVES[tag]),
        [field]: field === "opening" ? Number(value) || 0 : value
      }
    }));
  };

  /* ================= PANEL CONTROL ================= */
  const [openPanels, setOpenPanels] = useState([]);
  const openPanel = (tag) => {
    if (!openPanels.includes(tag)) {
      setOpenPanels(prev => [...prev, tag]);
    }
  };
  const closePanel = (tag) => {
    setOpenPanels(prev => prev.filter(p => p !== tag));
  };

  // ================= PUMP PANEL LOGIC =================
  const [openPumpPanels, setOpenPumpPanels] = useState([]);
  const openPumpPanel = (tag) => {
    if (!openPumpPanels.includes(tag)) {
      setOpenPumpPanels(prev => [...prev, tag]);
    }
  };
  const closePumpPanel = (tag) => {
    setOpenPumpPanels(prev => prev.filter(p => p !== tag));
  };

  const tagStyle = {
    position: "absolute",
    top: -30,
    left: "50%",
    transform: "translateX(-50%)",
    background: "black",
    color: "#00ffcc",
    fontSize: "13px",
    fontWeight: "bold",
    padding: "3px 8px",
    borderRadius: "4px",
    border: "1px solid #555",
    whiteSpace: "nowrap",
    zIndex: 999
  };

  // ================= RETREIVING OF DATA FROM OTHER PAGES VIA LOCAL STROAGE =================
  const { transmitters } = usePlantStore();
  const FIC_16010 = transmitters.FIC_16010 || 0;
  const TT_16005 = transmitters.TT_16005 || 0;
  const TT_2308 = transmitters.TT_2308 || 0;
  const TT_2408 = transmitters.TT_2408 || 0;




  const xv2001Open = !valves?.XV_2001;
  const xv2002Open = !valves?.XV_2002;

  const FIC_2001 = FIC_16010;
  const TT_2001 = TT_16005;
  const TT_2002 = xv2001Open ? TT_2408 : 28;
  const TT_2003 = xv2002Open ? TT_2308 : 28;

  
  
  /* =========================================================
   E-110 CRUDE ↔ DIESEL HEAT EXCHANGER
========================================================= */

const TT_2004 = processData.TT_2004;
const TT_2005 = processData.TT_2005;

/* =========================================================
   REFS
========================================================= */

const E110_intervalRef = useRef(null);
const E110_isMounted = useRef(false);

const E110_processRef = useRef({});

/* =========================================================
   KEEP LIVE VALUES UPDATED
========================================================= */

useEffect(() => {

  E110_processRef.current = {

    TT_2001,
    TT_2002

  };

});

/* =========================================================
   E-110 HEAT EXCHANGER
========================================================= */

useEffect(() => {

  if (E110_isMounted.current) return;

  E110_isMounted.current = true;

  console.log("✅ E-110 interval created");

  E110_intervalRef.current = setInterval(() => {

    const {

      TT_2001,
      TT_2002

    } = E110_processRef.current;

    /* =====================================
       INLETS
    ===================================== */

    const crudeInlet =
      Number(TT_2001) || 0;

    const dieselInlet =
      Number(TT_2002) || 0;

    let targetTT2004 = crudeInlet;
    let targetTT2005 = dieselInlet;

    /* =====================================
       HEAT EXCHANGE
    ===================================== */

    if (

      dieselInlet > crudeInlet + 0.5

    ) {

      const deltaT =
        dieselInlet - crudeInlet;

      const effectiveness = 0.45;

      const crudeGain = Math.min(

        11,

        deltaT * effectiveness

      );

      const dieselLoss = Math.min(

        25,

        deltaT * 0.40

      );

      targetTT2004 =
        crudeInlet + crudeGain;

      targetTT2005 =
        dieselInlet - dieselLoss;

      /* ===============================
         PHYSICAL LIMITS
      =============================== */

      targetTT2004 = Math.min(

        targetTT2004,

        dieselInlet - 5

      );

      targetTT2005 = Math.max(

        targetTT2005,

        crudeInlet + 5

      );

    }

    /* =====================================
       THERMAL LAG
    ===================================== */

    setProcessData(prev => ({

      ...prev,

      TT_2004: Number(

        (

          (prev.TT_2004 ?? crudeInlet)

          +

          (

            targetTT2004 -

            (prev.TT_2004 ?? crudeInlet)

          ) * 0.08

        ).toFixed(2)

      ),

      TT_2005: Number(

        (

          (prev.TT_2005 ?? dieselInlet)

          +

          (

            targetTT2005 -

            (prev.TT_2005 ?? dieselInlet)

          ) * 0.08

        ).toFixed(2)

      )

    }));

  }, 1000);

  return () => {

    console.log("❌ E-110 interval cleared");

    if (E110_intervalRef.current) {

      clearInterval(E110_intervalRef.current);

      E110_intervalRef.current = null;

    }

    E110_isMounted.current = false;

  };

}, []);




/* =========================================================
   E-111A/B : CRUDE ↔ ATMOSPHERIC RESIDUE
========================================================= */

const TT_2006 = processData.TT_2006;
const TT_2007 = processData.TT_2007;

/* =========================================================
   REFS
========================================================= */

const E111_intervalRef = useRef(null);
const E111_isMounted = useRef(false);

const E111_processRef = useRef({});

/* =========================================================
   KEEP LIVE VALUES UPDATED
========================================================= */

useEffect(() => {

  E111_processRef.current = {

    TT_2004,
    TT_2003

  };

});

/* =========================================================
   E-111 HEAT EXCHANGER
========================================================= */

useEffect(() => {

  if (E111_isMounted.current) return;

  E111_isMounted.current = true;

  console.log("✅ E-111 interval created");

  E111_intervalRef.current = setInterval(() => {

    const {

      TT_2004,
      TT_2003

    } = E111_processRef.current;

    /* =====================================
       INLETS
    ===================================== */

    const crudeInlet =
      Number(TT_2004) || 0;

    const residueInlet =
      Number(TT_2003) || 0;

    let targetTT2006 = crudeInlet;
    let targetTT2007 = residueInlet;

    /* =====================================
       HEAT EXCHANGE
    ===================================== */

    if (

      residueInlet > crudeInlet + 0.5

    ) {

      const deltaT =
        residueInlet - crudeInlet;

      /*
        Residue is much hotter than crude.
        Typical operation:
        - Crude gains ≈34°C
        - Residue loses ≈90°C
      */

      const crudeGain = Math.min(

        34,

        deltaT * 0.22

      );

      const residueLoss = Math.min(

        93,

        deltaT * 0.60

      );

      targetTT2006 =
        crudeInlet + crudeGain;

      targetTT2007 =
        residueInlet - residueLoss;

      /* ===============================
         PHYSICAL LIMITS
      =============================== */

      targetTT2006 = Math.min(

        targetTT2006,

        residueInlet - 10

      );

      targetTT2007 = Math.max(

        targetTT2007,

        crudeInlet + 10

      );

    }

    /* =====================================
       THERMAL LAG
    ===================================== */

    setProcessData(prev => ({

      ...prev,

      TT_2006: Number(

        (

          (prev.TT_2006 ?? crudeInlet)

          +

          (

            targetTT2006 -

            (prev.TT_2006 ?? crudeInlet)

          ) * 0.08

        ).toFixed(2)

      ),

      TT_2007: Number(

        (

          (prev.TT_2007 ?? residueInlet)

          +

          (

            targetTT2007 -

            (prev.TT_2007 ?? residueInlet)

          ) * 0.08

        ).toFixed(2)

      )

    }));

  }, 1000);

  return () => {

    console.log("❌ E-111 interval cleared");

    if (E111_intervalRef.current) {

      clearInterval(E111_intervalRef.current);

      E111_intervalRef.current = null;

    }

    E111_isMounted.current = false;

  };

}, []);




  // ✅ Update PlantStore without causing re-renders
  useEffect(() => {
    const current = PlantStore.get().transmitters;
    if (
      current.TT_2005 === TT_2005 &&
      current.TT_2006 === TT_2006 &&
      current.TT_2007 === TT_2007
    ) {
      return;
    }

    PlantStore.set({
      transmitters: {
        ...current,
        TT_2005,
        TT_2006,
        TT_2007
      }
    });
  }, [TT_2005, TT_2006, TT_2007]);

  return (
    <div className="FurnacePreHeat-container">
      {/* TITLE */}
      <h2 style={{
        position: "absolute",
        top: "0px",
        left: "240px",
        color: "white",
        background: "rgb(30, 5, 250)",
        padding: "2px 373px",
        zIndex: 10,
      }}>
        CRUDE TO FURNACE PREHEAT
      </h2>

      {/* =================SHUTDOWN VALVES ================= */}
      <Valve
        className="XV-2001"
        tag="XV_2001"
        x={267}
        y={148}
        isClosed={valves.XV_2001}
        toggle={toggleValve}
      />

      <Valve
        className="XV-2002"
        tag="XV_2002"
        x={1037}
        y={476}
        isClosed={valves.XV_2002}
        toggle={toggleValve}
      />



      
      <button
      
             onClick={() => {

        console.log("BUTTON CLICKED");

        setShowPerformance(true);

    }}
          style={{
              position: "absolute",
              right: "50px",
              top: "310px",
              padding: "8px 16px",
              background: "#0b5ed7",
              color: "#fff",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              fontWeight: "bold",
              zIndex: 500
          }}
      >
          Furnace Performance Report
      </button>


      {showPerformance && 
      
          <FurnacePerformance
              onClose={() => setShowPerformance(false)}
          />
      }





      {/* ================= CONTROL VALVE FACEPLATES ================= */}
      {openPanels.map((tag, index) => (
        <ControlValveControlPanel
          key={tag}
          tag={tag}
          data={valveData?.[tag] ?? DEFAULT_VALVES?.[tag]}
          update={updateValveData}
          close={() => closePanel(tag)}
          defaultPosition={{
            x: 300 + index * 40,
            y: 150 + index * 40
          }}
        />
      ))}

      {/* ================= PUMPS CONTROL VALVE FACEPLATES ================= */}
      {openPumpPanels.map((tag, index) => (
        <PumpFaceplate
          key={tag}
          tag={tag}
          close={() => closePumpPanel(tag)}
          defaultPosition={{
            x: 300 + index * 40,
            y: 150 + index * 40
          }}
        />
      ))}

      {/* ================= TRANSMITTERS ================= */}
      <Transmitter
        tag="FIC_2001"
        value={[FIC_2001]}
        x={262}
        y={352}
        unit="BPM"
        max={35}
        HH={34}
        H={30}
        L={0}
        LL={0}
        c="lime"
        description="FLOWRATE OF FLASHED CRUDE FROM BOTTOM OF PREFLASH TOWER TO E-110 BEFORE ENTERING FURNACE (F-101)"
      />

      <Transmitter
        tag="TT_2001"
        value={[TT_2001]}
        x={142}
        y={352}
        unit="°C"
        max={300}
        HH={286}
        H={265}
        L={0}
        LL={0}
        c="lime"
        description="TEMPERATURE OF FLASHED CRUDE AT THE BOTTOM OF PREFLASH FRACTIONATION TOWER (T-101)"
      />

      <Transmitter
        tag="TT_2003"
        value={[TT_2003]}
        x={1102}
        y={454}
        unit="°C"
        max={400}
        HH={386}
        H={365}
        L={0}
        LL={0}
        c="lime"
        description="TEMPERATURE OF REISDUE FROM P-108A/B TO E-111A/B"
      />

      <Transmitter
        tag="TT_2002"
        value={[TT_2002]}
        x={162}
        y={132}
        unit="°C"
        max={300}
        HH={286}
        H={265}
        L={0}
        LL={0}
        c="lime"
        description="TEMPERATURE OF DIESEL FROM P-112A/B TO E-110"
      />

      <Transmitter
        tag="TT_2004"
        value={[TT_2004]}
        x={492}
        y={110}
        unit="°C"
        max={300}
        HH={286}
        H={265}
        L={0}
        LL={0}
        c="lime"
        description="TEMPERATURE OF FLASHED CRUDE FROM E-110 TO E-111A/B"
      />

      <Transmitter
        tag="TT_2005"
        value={[TT_2005]}
        x={302}
        y={436}
        unit="°C"
        max={300}
        HH={286}
        H={265}
        L={0}
        LL={0}
        c="lime"
        description="TEMPERATURE OF DIESEL FROM E-110 TO E-106A/B"
      />

      <Transmitter
        tag="TT_2006"
        value={[TT_2006]}
        x={952}
        y={556}
        unit="°C"
        max={300}
        HH={286}
        H={265}
        L={0}
        LL={0}
        c="lime"
        description="TEMPERATURE OF CRUDE FROM E-111A/B TO F-101"
      />

      <Transmitter
        tag="TT_2007"
        value={[TT_2007]}
        x={1042}
        y={86}
        unit="°C"
        max={300}
        HH={286}
        H={265}
        L={0}
        LL={0}
        c="lime"
        description="TEMPERATURE OF ATM RESIDUE FROM E-111A/B TO E-107A/B"
      />
    </div>
  );
}

export default memo(FurnacePreHeat);